import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Wheat } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api, { platformAPI } from '@/lib/api';
import HomeShell from '@/components/home/HomeShell';
import HomeTiles from '@/components/home/HomeTiles';
import HomeLatestDeals from '@/components/home/HomeLatestDeals';
import HomeBigButtons from '@/components/home/HomeBigButtons';
import { HomeSearchBar, HomeStakeholderStrip } from '@/components/home/HomeBlocks';

// Normalise a Promise.allSettled result into a plain array of rows,
// tolerant of both raw-array responses and the paginated {items:[…]}
// shape that some endpoints return.
function _rowsOf(res) {
  if (res.status !== 'fulfilled') return [];
  const data = res.value?.data;
  if (Array.isArray(data)) return data;
  return data?.items || [];
}

// People circles, search, the two big yellow buttons (products and farm
// produce marketplaces), the latest deals from his distributor, tiles.
// No "My statistics" box (beta feedback).
export default function ShopkeeperHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [deals, setDeals] = useState([]);
  const [platform, setPlatform] = useState({});
  const [searchQ, setSearchQ] = useState('');

  const loadData = useCallback(async () => {
    const [dealRes, platRes] = await Promise.allSettled([
      // Deals reaching a shop: his own distributor's (deal_reach, PR 72).
      api.get(`/deals/for-user/${user?.id}`, { params: { sort: 'newest', limit: 10 } }),
      platformAPI.getStatistics(),
    ]);
    setDeals(_rowsOf(dealRes));

    if (platRes.status === 'fulfilled') setPlatform(platRes.value.data || {});
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/inventory?search=${encodeURIComponent(q)}` : '/inventory');
  };

  return (
    <HomeShell>
      <HomeStakeholderStrip platform={platform} t={t} />
      <HomeSearchBar
        value={searchQ}
        onChange={setSearchQ}
        onSubmit={onSearchSubmit}
        testId="shopkeeper-home-search-input"
      />
      <HomeBigButtons buttons={[
        { icon: ShoppingCart, label: t('distributorHome.productsMarketplace'), onClick: () => navigate('/marketplace'),
          testId: 'shopkeeper-products-marketplace' },
        { icon: Wheat, label: t('distributorHome.produceMarketplace'), onClick: () => navigate('/produce'),
          testId: 'shopkeeper-produce-marketplace' },
      ]} />
      <HomeLatestDeals deals={deals} />
      <HomeTiles role="shopkeeper" />
    </HomeShell>
  );
}
